import { Button, Modal, Progress, Table, Tag, Typography, message } from 'antd';
import React, { useEffect, useRef, useState } from 'react';
import { playlistApiService } from '../services/playlistApiService';
import {
  FeedValidation,
  FeedValidationDetail as FeedValidationDetailType,
  FeedValidationInvalidItem,
} from '../types/playlistType';

const { Text } = Typography;

interface Props {
  /** The run to show; null keeps the modal closed. */
  validation: FeedValidation | null;
  playlistId: number;
  onClose: () => void;
}

export const FeedValidationDetail: React.FC<Props> = ({ validation, playlistId, onClose }) => {
  const [detail, setDetail] = useState<FeedValidationDetailType | null>(null);
  const [loading, setLoading] = useState(false);
  const [recoveringIds, setRecoveringIds] = useState<Set<number>>(new Set());
  const [recoverAllDone, setRecoverAllDone] = useState<number | null>(null);

  // Recover All is a sequential loop; stop it if the page goes away mid-run.
  const isMountedRef = useRef(true);
  useEffect(
    () => () => {
      isMountedRef.current = false;
    },
    []
  );

  useEffect(() => {
    if (!validation) return;
    setDetail(null);
    setLoading(true);
    playlistApiService
      .fetchValidationDetail(playlistId, validation.id)
      .then(setDetail)
      .catch(() => message.error('Failed to load validation result'))
      .finally(() => setLoading(false));
  }, [validation, playlistId]);

  const handleRecover = async (videoId: number) => {
    setRecoveringIds(prev => new Set(prev).add(videoId));
    try {
      const result = await playlistApiService.recoverVideo(videoId);
      message.success(result.message || 'Recovery started');
    } catch {
      message.error('Recovery failed');
    } finally {
      setRecoveringIds(prev => {
        const next = new Set(prev);
        next.delete(videoId);
        return next;
      });
    }
  };

  const invalidItems = detail?.invalid_items ?? [];
  const recoverableItems = invalidItems.filter(item => item.video_id !== null);

  const handleRecoverAll = async () => {
    const total = recoverableItems.length;
    let failed = 0;
    for (let i = 0; i < total; i++) {
      if (!isMountedRef.current) return;
      setRecoverAllDone(i);
      try {
        await playlistApiService.recoverVideo(recoverableItems[i].video_id as number);
      } catch {
        failed += 1;
      }
    }
    if (!isMountedRef.current) return;
    setRecoverAllDone(null);
    // Recovery itself runs as Batch jobs, so re-validating right away would still see the old
    // files. Tell the operator to come back instead.
    const summary = `Recovery submitted for ${total - failed}/${total} items.`;
    const next = 'Run Validate Feed again once the recovery jobs finish.';
    if (failed) {
      message.warning(`${summary} ${next}`);
    } else {
      message.success(`${summary} ${next}`);
    }
  };

  const columns = [
    {
      title: '#',
      key: 'index',
      width: 50,
      render: (_: unknown, __: unknown, index: number) => index + 1,
    },
    { title: 'Title', dataIndex: 'title', key: 'title', ellipsis: true },
    { title: 'Content ID', dataIndex: 'content_id', key: 'content_id', width: 110 },
    {
      title: 'Action',
      key: 'action',
      width: 130,
      render: (_: unknown, record: FeedValidationInvalidItem) => {
        if (record.video_id !== null) {
          const videoId = record.video_id;
          return (
            <Button
              type="link"
              loading={recoveringIds.has(videoId)}
              onClick={() => handleRecover(videoId)}
            >
              Recover
            </Button>
          );
        }
        const path =
          record.content_type === 'movie'
            ? `/movie/edit/${record.content_id}`
            : `/episode/edit/${record.content_id}`;
        return (
          <Button type="link" onClick={() => window.open(path, '_blank')}>
            View Content
          </Button>
        );
      },
    },
  ];

  const recoveringAll = recoverAllDone !== null;

  return (
    <Modal
      title="Feed Validation Result"
      open={Boolean(validation)}
      onCancel={onClose}
      footer={null}
      width={800}
    >
      {detail && (
        <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
          <Tag>Total: {detail.total_count}</Tag>
          <Tag color="success">Valid: {detail.valid_count}</Tag>
          <Tag color="error">Invalid: {detail.invalid_count}</Tag>
        </div>
      )}
      {recoverAllDone !== null && (
        <Progress
          status="active"
          percent={Math.round((recoverAllDone / recoverableItems.length) * 100)}
          format={() => `${recoverAllDone}/${recoverableItems.length}`}
        />
      )}
      <Table
        dataSource={invalidItems}
        columns={columns}
        rowKey="id"
        loading={loading}
        pagination={{ pageSize: 20 }}
        size="small"
        title={() => <Text strong>Invalid Items</Text>}
      />
      {recoverableItems.length >= 2 && (
        <div style={{ textAlign: 'center', marginTop: '16px' }}>
          <Button type="primary" onClick={handleRecoverAll} loading={recoveringAll}>
            Recover All ({recoverableItems.length} items)
          </Button>
        </div>
      )}
    </Modal>
  );
};
